import { useEffect, useRef, useState } from 'react'

import { BaseTextProps } from '@trustycars/genie-ui/components/fields'

import { DEFAULT_OTP_CODE_LENGTH } from '$constants'
import { isNumeric, wait } from '$app/utilities'

type useOTPInputOptions = {
  className?: string
  length?: number
  disabled?: boolean
  onFilled: (passwords: string, reset: VoidFunction) => void
  autoFocus?: boolean
  defaultValue?: string
}

export const useOTPInput = (options: useOTPInputOptions) => {
  const {
    defaultValue = '',
    length = DEFAULT_OTP_CODE_LENGTH,
    disabled,
    onFilled,
    autoFocus
  } = options

  const emptyPasswords = defaultValue

  const containerRef = useRef<HTMLElement>(null)
  const [passwords, setPasswords] = useState(emptyPasswords)

  const safeValues = passwords.split('').filter(isNumeric)
  const isFilled = length === safeValues.length
  const formattedValues = safeValues.join('')

  const passwordFieldProps: BaseTextProps = {
    disabled,
    inputProps: {
      value: formattedValues,
      inputMode: 'numeric',
      step: 1,
      min: 0,
      max: 9,
      maxLength: length,
      autoComplete: 'one-time-code',
      placeholder: 'Enter your OTP code',
      autoFocus,

      onChange: e => {
        const value = e.target.value || ''
        setPasswords(value)
      },
      onPaste: e => {
        e.preventDefault()

        setPasswords(e.clipboardData.getData('text'))
      }
    }
  }

  const resetPasswords = () => setPasswords(emptyPasswords)

  useEffect(() => {
    if (!isFilled) return

    onFilled(formattedValues, resetPasswords)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFilled, formattedValues])

  // WebOTP
  useEffect(() => {
    // On non-localhost http, credentials could be undefined
    if (!navigator.credentials || disabled) return

    const controller = new AbortController()

    // Wait OTP
    navigator.credentials
      .get({
        signal: controller.signal,

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        otp: {
          transport: ['sms']
        }
      })
      .then(otp => {
        if (!otp || controller.signal.aborted) return

        // @ts-expect-error cuz i know what i'm doing
        setPasswords(otp.code)
      })
      // avoid throwing error
      .catch(() => {})

    return () => {
      const run = async () => {
        // TODO: Chromium bug fix: To prevent crashes.
        // Reason:
        // Chromium can't abort the event immediately. In this case, crashes the tab.
        // This happens mainly in localhost.
        // Certain conditions trigger this: Oh yah its our boy React again?
        // - Partly because of how React.strictmode behave.
        // - This hook was originally extracted from OTPInput element component:
        //   - It didn't happen when it was a component,
        //   - That means custom react hook's lifecycle works a little bit different than component lifecycle.
        //   - Different in terms of speed: Hook's lifecycle runs faster than component.
        //
        // We'll only wait on DEV mode where Strict mode is active.
        if (import.meta.env.DEV) {
          // Noting can beat a good ol' delay.
          await wait(500)
        }

        controller.abort('Stopped listening otp')
      }

      run()
    }
  }, [disabled])

  return {
    ...options,
    containerRef,
    resetPasswords,
    passwordFieldProps
  }
}

export default useOTPInput
